import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { AssetApiClient } from '@loyalty/shared-common/asset-api-client';
import { AxiosInstance } from 'axios';
import { CampaignFeedApiClient } from '@loyalty/shared-common/campaign-feed-api-client';
import { DataSearchApiClient } from '@qualifio/data-search-api-client';
import { EventProcessingApiClient } from '@loyalty/shared-common/event-processing-api-client';
import { GamificationApiClient } from '@loyalty/shared-common/gamification-api-client';
import { IngestorApiClient } from '@loyalty/shared-common/ingestor-api-client';
import { IntegrationApiClient } from '@loyalty/shared-common/integration-api-client';
import { ManagerApiClient } from '@loyalty/shared-common/manager-api-client';
import { PremiumGamesApiClient } from '@loyalty/shared-common/premium-games-api-client';
import { ProfileApiClient } from '@loyalty/shared-common/profile-api-client';
import { ReportApiClient } from '@loyalty/shared-common/report-api-client';
import { RewardsApiClient } from '@loyalty/shared-common/rewards-api-client';
import { RuleApiClient } from '@loyalty/shared-common/rule-api-client';
import { SsoProxyApiClient } from '@qualifio/sso-proxy-api-client';
import { IntegrationApiClient as SsoProxyIntegrationApiClient } from '@qualifio/sso-proxy-ui';
import { TemplateApiClient } from '@loyalty/shared-common/template-api-client';
import { configureApiClient } from '@qualifio/api-client/dist/browser';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { Loader } from '../layout/Loader';
import { useConfiguration } from '../../ConfigurationContext';

export interface ApiClientAttribute {
  assetApi: AssetApiClient;
  eventProcessingApi: EventProcessingApiClient;
  gamificationApi: GamificationApiClient;
  ingestorApi: IngestorApiClient;
  integrationApi: IntegrationApiClient;
  managerApi: ManagerApiClient;
  premiumGamesApi: PremiumGamesApiClient;
  profileApi: ProfileApiClient;
  reportApi: ReportApiClient;
  rewardsApi: RewardsApiClient;
  ruleApi: RuleApiClient;
  templateApi: TemplateApiClient;
  dataSearchApi: DataSearchApiClient;
  ssoProxyApi: SsoProxyApiClient;
  ssoProxyIntegrationApiClient: SsoProxyIntegrationApiClient;
  campaignFeedApi: CampaignFeedApiClient;
}

const ApiClientContext = createContext<ApiClientAttribute>({} as any);

export function useApi() {
  return useContext(ApiClientContext);
}

export function ApiClientProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const [api, setApi] = useState<ApiClientAttribute>();
  const { qualifioApiUrl } = useConfiguration();

  useEffect(() => {
    const pathPrefix = BASE_PATH;
    const headers = LOCAL_JWT_TOKEN ? { Authorization: `Bearer ${LOCAL_JWT_TOKEN}` } : undefined;
    const apis = {
      assetApi: configureApiClient(AssetApiClient, { pathPrefix, headers }),
      eventProcessingApi: configureApiClient(EventProcessingApiClient, {
        pathPrefix,
        headers,
      }),
      gamificationApi: configureApiClient(GamificationApiClient, {
        pathPrefix,
        headers,
      }),
      ingestorApi: configureApiClient(IngestorApiClient, { pathPrefix, headers }),
      integrationApi: configureApiClient(IntegrationApiClient, { headers }),
      managerApi: configureApiClient(ManagerApiClient, { pathPrefix, headers }),
      premiumGamesApi: configureApiClient(PremiumGamesApiClient, { headers }),
      profileApi: configureApiClient(ProfileApiClient, { pathPrefix, headers }),
      reportApi: configureApiClient(ReportApiClient, { pathPrefix, headers }),
      rewardsApi: configureApiClient(RewardsApiClient, { pathPrefix, headers }),
      ruleApi: configureApiClient(RuleApiClient, { pathPrefix, headers }),
      templateApi: configureApiClient(TemplateApiClient, { pathPrefix, headers }),
      dataSearchApi: configureApiClient<any>(DataSearchApiClient, { headers }),
      ssoProxyApi: configureApiClient<any>(SsoProxyApiClient, { headers }),
      ssoProxyIntegrationApiClient: configureApiClient<any>(SsoProxyIntegrationApiClient, {
        headers,
      }),
      campaignFeedApi: configureApiClient(CampaignFeedApiClient, {
        pathPrefix: qualifioApiUrl,
        headers,
      }),
    };

    if (LOCAL_JWT_TOKEN) {
      (apis.dataSearchApi.http as AxiosInstance).defaults.headers.common.Authorization =
        headers?.Authorization;
      (apis.ssoProxyApi.http as AxiosInstance).defaults.headers.common.Authorization =
        headers?.Authorization;
      (
        apis.ssoProxyIntegrationApiClient.http as AxiosInstance
      ).defaults.headers.common.Authorization = headers?.Authorization;
    }

    setApi(apis);
  }, [notify, t]);

  return !api ? (
    <Loader />
  ) : (
    <ApiClientContext.Provider value={api}>{children}</ApiClientContext.Provider>
  );
}
